import React, { useState, useEffect, useCallback, useRef } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { TID } from "@/constants/tids";
import { logError } from "@/lib/logger";
import { rangeParams, RANGE_OPTIONS } from "@/lib/dateRanges";
import { useNodeExport } from "@/hooks/useNodeExport";
import TeamPrint from "@/components/team/TeamPrint";
import TabLoadError from "@/components/shared/TabLoadError";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Users, Loader2, Sparkles, CalendarRange, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const REMARK_PLUGINS = [remarkGfm];
const ACT = ["calls", "emails", "tasks", "meetings", "notes"];

export default function Team({ range, onRangeChange, onRegisterExport }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const printRef = useRef(null);
  const rangeLabel = RANGE_OPTIONS.find((o) => o.value === range)?.label || "All Time";

  const { exportPdf, printNode } = useNodeExport(
    printRef,
    () => !!(data && data.reps && data.reps.length),
    "salestap-team"
  );

  useEffect(() => {
    if (!onRegisterExport) return;
    onRegisterExport("team", { exportPdf, printFn: printNode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterExport, exportPdf, printNode, data]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { start, end } = rangeParams(range);
      // Primary data must surface errors; a missing AI report is normal.
      const [t, rep] = await Promise.all([
        api.getTeam(start, end),
        api.getTeamReport().catch(() => ({ report_md: null })),
      ]);
      setData(t);
      setReport(rep?.report_md || null);
    } catch (e) {
      logError("Failed to load team:", e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    setReportLoading(true);
    try {
      const { start, end } = rangeParams(range);
      await api.generateTeamReport(start, end);
      const t0 = Date.now();
      const poll = async () => {
        const st = await api.teamReportStatus();
        if (st.status === "done") {
          setReport(st.report_md);
          setReportLoading(false);
          toast.success("Team analysis complete.");
          return;
        }
        if (st.status === "error") {
          setReportLoading(false);
          toast.error(st.error || "Analysis failed.");
          return;
        }
        if (Date.now() - t0 > 180000) {
          setReportLoading(false);
          toast.error("Analysis timed out.");
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setReportLoading(false);
      toast.error(e.response?.data?.detail || "Analysis failed.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (loadError) {
    return <TabLoadError onRetry={load} />;
  }

  const reps = data?.reps || [];
  const totals = data?.totals || {};
  const details = data?.details || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          <div>
            <h2 className="font-semibold text-foreground leading-tight">Reps / Team</h2>
            <p className="text-xs text-muted-foreground">
              {totals.reps || 0} reps · {totals.win_rate ?? 0}% team win rate · {fmtCurrency(totals.won_value)} won
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CalendarRange className="w-4 h-4 text-muted-foreground" />
          <Select value={range} onValueChange={onRangeChange}>
            <SelectTrigger className="w-[160px]" data-testid="team-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid={TID.genTeamReportBtn} onClick={generate} disabled={reportLoading || !reps.length} variant="outline" size="sm">
            {reportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {report ? "Regenerate" : "AI Analysis"}
          </Button>
        </div>
      </div>

      {reps.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          No rep data for this period. (Requires a live HubSpot connection with deals/owners.)
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Rep Scorecard <span className="text-xs font-normal text-muted-foreground ml-1">· {rangeLabel}</span></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto" data-testid={TID.teamScorecard}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rep</TableHead>
                      <TableHead className="text-right">Won</TableHead>
                      <TableHead className="text-right">Open</TableHead>
                      <TableHead className="text-right">Win%</TableHead>
                      <TableHead className="text-right">Avg Deal</TableHead>
                      <TableHead className="text-right">Avg Cycle</TableHead>
                      <TableHead className="text-right">Activities</TableHead>
                      <TableHead className="text-right">Act/Win</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reps.map((r) => {
                      const isOpen = expanded === r.owner_id;
                      const det = details[r.owner_id] || {};
                      return (
                        <React.Fragment key={r.owner_id}>
                          <TableRow
                            data-testid={TID.teamRepRow}
                            className="cursor-pointer"
                            onClick={() => setExpanded(isOpen ? null : r.owner_id)}
                          >
                            <TableCell className="font-medium text-foreground">
                              <span className="inline-flex items-center gap-1">
                                {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                {r.name}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">{fmtCurrency(r.won_value)}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{fmtCurrency(r.open_value)}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant={r.win_rate >= 50 ? "default" : "secondary"}>{r.win_rate}%</Badge>
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">{fmtCurrency(r.avg_deal_size)}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{r.avg_cycle_days != null ? `${r.avg_cycle_days}d` : "—"}</TableCell>
                            <TableCell className="text-right">{fmtNumber(r.activities)}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{r.activities_per_win ?? "—"}</TableCell>
                          </TableRow>
                          {isOpen && (
                            <TableRow>
                              <TableCell colSpan={8} className="bg-muted/40">
                                <div className="grid md:grid-cols-2 gap-4 py-2">
                                  <div>
                                    <p className="text-xs font-semibold text-foreground mb-1">Activity breakdown</p>
                                    <div className="flex flex-wrap gap-2">
                                      {ACT.map((t) => (
                                        <span key={t} className="text-xs bg-background border border-border rounded px-2 py-0.5 capitalize">
                                          {t}: <span className="font-medium">{fmtNumber((det.activities_by_type || {})[t] || 0)}</span>
                                        </span>
                                      ))}
                                    </div>
                                    <p className="text-xs font-semibold text-foreground mt-3 mb-1">
                                      Overdue ({(det.overdue_deals || []).length}) · Stalled ({(det.stalled_deals || []).length})
                                    </p>
                                    <div className="space-y-1 max-h-[120px] overflow-y-auto">
                                      {(det.overdue_deals || []).slice(0, 5).map((d) => (
                                        <div key={d.id} className="text-xs flex justify-between">
                                          <span className="truncate mr-2 text-foreground/80">{d.name}</span>
                                          <span className="text-primary flex-shrink-0">{d.days_overdue}d overdue</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-xs font-semibold text-foreground mb-1">
                                      Open deals ({(det.open_deals || []).length})
                                    </p>
                                    <div className="space-y-1 max-h-[160px] overflow-y-auto">
                                      {(det.open_deals || []).slice(0, 8).map((d) => (
                                        <div key={d.id} className="text-xs flex justify-between border-b border-border/60 pb-0.5">
                                          <span className="truncate mr-2 text-foreground/80">{d.name} · {d.stage}</span>
                                          <span className="flex-shrink-0 font-medium">{fmtCurrency(d.amount)}</span>
                                        </div>
                                      ))}
                                      {(det.open_deals || []).length === 0 && (
                                        <p className="text-xs text-muted-foreground">No open deals.</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {report && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" /> AI Team Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <article data-testid={TID.teamReportContent} className="prose prose-slate dark:prose-invert max-w-none prose-h2:text-lg prose-strong:text-foreground">
                  <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{report}</ReactMarkdown>
                </article>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <TeamPrint ref={printRef} team={data} reportMd={report} rangeLabel={rangeLabel} />
    </div>
  );
}
